import { useTranslation } from 'react-i18next';

export interface PasswordValidation {
  isValid: boolean;
  strength: 'weak' | 'medium' | 'strong';
  minLength: boolean;
  hasUpper: boolean;
  hasLower: boolean;
  hasNumber: boolean;
  hasSpecial: boolean;
}

const SPECIAL_CHAR_REGEX = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/;

export function getPasswordValidation(password: string): PasswordValidation {
  const minLength = password.length >= 12;
  const hasUpper = /[A-Z]/.test(password);
  const hasLower = /[a-z]/.test(password);
  const hasNumber = /[0-9]/.test(password);
  const hasSpecial = SPECIAL_CHAR_REGEX.test(password);

  const metRequirements = [minLength, hasUpper, hasLower, hasNumber, hasSpecial].filter(
    Boolean
  ).length;

  let strength: PasswordValidation['strength'] = 'weak';
  if (metRequirements === 5) {
    strength = 'strong';
  } else if (metRequirements >= 3) {
    strength = 'medium';
  }

  return {
    isValid: minLength && hasUpper && hasLower && hasNumber && hasSpecial,
    strength,
    minLength,
    hasUpper,
    hasLower,
    hasNumber,
    hasSpecial,
  };
}

interface PasswordRequirementsProps {
  password: string;
  translationKeyPrefix: string;
  ns?: string;
}

export function PasswordRequirements({
  password,
  translationKeyPrefix,
  ns = 'auth',
}: PasswordRequirementsProps) {
  const { t } = useTranslation(ns);
  const validation = getPasswordValidation(password);

  const requirements = [
    { key: 'minLength', met: validation.minLength },
    { key: 'hasUpper', met: validation.hasUpper },
    { key: 'hasLower', met: validation.hasLower },
    { key: 'hasNumber', met: validation.hasNumber },
    { key: 'hasSpecial', met: validation.hasSpecial },
  ];

  return (
    <div className="mt-4 px-1">
      <div className="flex items-center space-x-2 mb-3">
        <div className="flex-1 h-1.5 bg-surface-2-light dark:bg-surface-2-dark rounded-full overflow-hidden">
          <div
            className={`h-full transition-all duration-500 ${
              validation.strength === 'weak'
                ? 'w-1/3 bg-state-danger-light dark:bg-state-danger-dark'
                : validation.strength === 'medium'
                  ? 'w-2/3 bg-state-warn-light dark:bg-state-warn-dark'
                  : 'w-full bg-state-ok-light dark:bg-state-ok-dark'
            }`}
          />
        </div>
        <span className="text-[10px] uppercase tracking-wider font-black text-text-subtle-light dark:text-text-subtle-dark">
          {t(`passwordStrength.${validation.strength}`)}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-x-4 gap-y-2">
        {requirements.map(({ key, met }) => (
          <Requirement
            key={key}
            met={met}
            text={t(`${translationKeyPrefix}.${key}`)}
            testId={`req-${key}`}
          />
        ))}
      </div>
    </div>
  );
}

interface RequirementProps {
  met: boolean;
  text: string;
  testId: string;
}

function Requirement({ met, text, testId }: RequirementProps) {
  return (
    <div
      data-testid={testId}
      className={`flex items-center space-x-1.5 ${met ? 'text-state-ok-light dark:text-state-ok-dark' : 'text-slate-300 dark:text-slate-700'}`}
    >
      <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        {met ? (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={4} d="M5 13l4 4L19 7" />
        ) : (
          <circle cx="12" cy="12" r="10" strokeWidth={2} />
        )}
      </svg>
      <span
        className={`text-[11px] font-bold ${met ? 'text-slate-700 dark:text-slate-300' : 'text-text-subtle-light dark:text-text-subtle-dark'}`}
      >
        {text}
      </span>
    </div>
  );
}
